// Campaign backup step — keeps the "Backup for" picker in step with the
// step's channel.
//
// A backup goes out on the other channel from the step it backs up, so
// the group on the step's own channel comes out of the select: Safari
// ignores hidden on an <option>. The choice is held across a channel
// switch, as typed text is, and comes back with its group. The field
// hides when nothing is left to pick, unless the step was saved as a
// backup, so a dropped link stays visible.
//
// Targets: [data-backup-field].

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const CHANNEL_SMS = "sms";

export function initCampaignBackup(root = document) {
  root.querySelectorAll("[data-backup-field]").forEach((field) => {
    if (!firstBind(field)) return;

    const form = field.closest("form");
    const channel = form?.querySelector('[name="channel"]');
    const select = field.querySelector("select");
    const groups = [
      ...select.querySelectorAll("optgroup[data-backup-channel]"),
    ];
    const hint = field.querySelector("[data-backup-hint]");
    const warning = field.querySelector("[data-backup-warning]");
    const cleared = field.querySelector("[data-backup-cleared]");
    let held = select.value;

    // Keyed on the held choice, not the select's value: a saved primary
    // on the step's own channel leaves the select empty on load, and the
    // warning is what explains why. aria-describedby reads a hidden node,
    // so each note's reference comes and goes with it.
    const syncNotes = () => {
      if (warning) warning.hidden = held !== warning.dataset.backupWarning;
      // Keyed on what the save will post, so a channel switch that empties
      // the select says so too.
      if (cleared) cleared.hidden = select.value !== "";

      const shown = [hint, warning, cleared].filter(
        (note) => note && !note.hidden,
      );
      select.setAttribute(
        "aria-describedby",
        shown.map((note) => note.id).join(" "),
      );
    };

    const sync = () => {
      const current = channel?.value || "";
      let selectable = 0;
      groups.forEach((group) => {
        group.remove();
        if (group.dataset.backupChannel === current) return;

        select.append(group);
        selectable += group.children.length;
      });
      select.value = held;
      if (select.selectedIndex < 0) select.value = "";

      field.hidden = selectable === 0 && !("backupSaved" in field.dataset);
      hint.textContent =
        current === CHANNEL_SMS ? hint.dataset.hintSms : hint.dataset.hintEmail;
      syncNotes();
    };

    // The form's own bubbled change, so a select elsewhere on the page
    // never re-syncs this one.
    form?.addEventListener("change", (event) => {
      if (event.target === channel) sync();
    });
    select.addEventListener("change", () => {
      held = select.value;
      syncNotes();
    });

    sync();
  });
}
